// frontend/src/hooks/useVisitReadOnly.js
// Returns true if the given visitId should be READ-ONLY in the current context.
// Editable when either:
//   (A) this route's visitId equals the active visit for the scoped citizen, OR
//   (B) we're in an explicit "returned visit edit session" AND the user is an intern.

import { useMemo } from 'react';

const jget = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || 'null'); }
  catch { return null; }
};

function resolveCitizenId(explicitCitizenId) {
  if (explicitCitizenId) return explicitCitizenId;
  const activeCitizen = jget('activeCitizen');
  if (activeCitizen?.citizenId) return activeCitizen.citizenId;
  const cid = localStorage.getItem('activeCitizenId') || localStorage.getItem('citizenId');
  return cid || '';
}

function resolveActiveVisitId(citizenId) {
  // Per-citizen scoped active visit takes precedence
  const scoped = citizenId ? localStorage.getItem(`activeVisitId:${citizenId}`) : null;
  if (scoped) return scoped;

  // Legacy fallbacks
  const v1 = localStorage.getItem('activeVisitId');
  if (v1) return v1;
  const v2 = localStorage.getItem('visitId');
  if (v2) return v2;

  return '';
}

function isIntern() {
  const u = jget('loggedInUser') || {};
  const role = String(u.role || u.userType || '').toLowerCase();
  return role.includes('intern');
}

/**
 * useVisitReadOnly
 * @param {string} visitId
 * @param {string} [citizenId]
 * @returns {boolean} readOnly
 */
export default function useVisitReadOnly(visitId, citizenId) {
  return useMemo(() => {
    if (!visitId) return true;

    // (A) Normal rule: editable when this route's visitId equals the active visit
    const cid = resolveCitizenId(citizenId);
    const active = resolveActiveVisitId(cid);
    const activeMatch = Boolean(active) && String(active) === String(visitId);

    // (B) Returned-edit session override (interns only)
    // When an intern opens a "returned" visit from lists, code sets:
    //   localStorage.setItem('editReturnedVisit', visitId)
    const editSession = localStorage.getItem('editReturnedVisit');
    const sessionMatch = !!editSession && String(editSession) === String(visitId);
    const allowReturnedEdit = sessionMatch && isIntern();

    // Read-only unless one of the allow rules is true
    return !(activeMatch || allowReturnedEdit);
  }, [visitId, citizenId]);
}
